import React, { useState } from 'react';
import { Check, ClipboardCheck, FolderKanban, Heart, Phone } from 'lucide-react';
import { parseCalDate } from '../../utils/calendarDate';
import { resolvePhotoUrl } from '../../utils/photoUtils';

// 客户管道看板 — faithful build of the CRM design mockup (page 1): a framed
// "screen" with a toolbar, a column-header row, and clients grouped by pipeline
// stage as rows with 客户 / 负责红娘 / 阶段 / 本周联系 / 下次联系 / 约会结果 /
// 会员到期 / 备注. Draggable rows change stage; clicking a row opens the client
// detail.
//
// Grouping is by `pipeline_stage` (the matchmaking funnel), NOT by `status` (the
// membership state). Those are different questions — nearly every client is
// status=active, so grouping by status collapsed the board into one pile.
//
// Props: profiles, loading, onCardClick(id), onStageChange(id, stageKey),
//        isZh, language,
//        oversight (viewer sees every coach's clients rather than their own),
//        truncatedAt (row cap the fetch hit, if any).
//
// Scope is the caller's call, not a control in here: a coach fetches with
// ?mine=1 (their own coaching book), oversight roles fetch the full book.

const APP = { bg: '#FFFDF9', alt: '#F6F3EE', line: '#E4DFD7', ink: '#111111', muted: '#6E685F', burgundy: '#111111' };
const AV_PALETTE = ['#6B4E71', '#3D6B6B', '#9E2B25', '#93591A', '#3E5C76', '#3F7D5A'];
const avColor = (n) => AV_PALETTE[(Math.abs(Number(n) || 0)) % AV_PALETTE.length];

// The funnel, in order. Keys match the backend's PIPELINE_STAGES whitelist
// (routes/index.js) and the migration backfill — keep the three in sync.
export const STAGES_DEF = (isZh) => ([
  { key: 'new',         label: isZh ? '新客户' : 'New',          color: '#3E5C76' },
  { key: 'contacting',  label: isZh ? '沟通中' : 'Contacting',   color: '#3D6B6B' },
  { key: 'recommended', label: isZh ? '已推荐' : 'Recommended',  color: '#6B4E71' },
  { key: 'dating',      label: isZh ? '约会中' : 'Dating',       color: '#93591A' },
  { key: 'success',     label: isZh ? '成功' : 'Success',        color: '#3F7D5A' },
  { key: 'paused',      label: isZh ? '暂停' : 'Paused',         color: '#93591A' },
  { key: 'ended',       label: isZh ? '已结束' : 'Ended',        color: '#C9C3BA' },
]);

const DATE_STATUS = {
  pending: { zh: '待约会', en: 'Pending', c: '#6E685F' }, confirmed: { zh: '已确认', en: 'Confirmed', c: '#5B7C8F' },
  accepted: { zh: '已接受', en: 'Accepted', c: '#3F7D5A' }, meeting: { zh: '见面了', en: 'Met', c: '#6B4E71' },
  chatting: { zh: '聊天中', en: 'Chatting', c: '#3D6B6B' }, in_relationship: { zh: '交往中', en: 'Together', c: '#8E4A5E' },
  engaged: { zh: '已订婚', en: 'Engaged', c: '#93591A' }, completed: { zh: '已完成', en: 'Done', c: '#6E685F' },
  cancelled: { zh: '已取消', en: 'Cancelled', c: '#6E685F' }, no_show: { zh: '未到场', en: 'No-show', c: '#9E2B25' },
  rejected: { zh: '已拒绝', en: 'Rejected', c: '#9E2B25' },
};

const COLS = 'minmax(150px,1.4fr) 96px 92px 78px 92px 104px 92px minmax(120px,1.1fr)';
const displayIdOf = (p) => {
  if (p.offline_id && String(p.offline_id) !== '0' && String(p.offline_id) !== 'null') return p.offline_id;
  return p.id;
};
const pill = (color) => ({ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#FFFDF9', background: color, borderRadius: 2, padding: '4px 9px', whiteSpace: 'nowrap' });

// Compact label for 下次联系 — real timestamp, shown as 今天/明天/M月D (or M/D),
// flagged overdue (red) when the touchpoint is already past-due.
const fmtNextContact = (iso, isZh) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const days = Math.round((startOf(d) - startOf(now)) / 86400000);
  const overdue = d < now;
  let label;
  if (days === 0) label = isZh ? '今天' : 'Today';
  else if (days === 1) label = isZh ? '明天' : 'Tmrw';
  else if (days === -1) label = isZh ? '昨天' : 'Yest.';
  else label = isZh ? `${d.getMonth() + 1}月${d.getDate()}` : `${d.getMonth() + 1}/${d.getDate()}`;
  return { label, overdue };
};

// 约会结果 — the design's derived states, not the raw schedule status:
//   no date at all              → 未安排 (grey)
//   date already happened, no feedback written → 待反馈 (amber, actionable)
//   otherwise                   → the schedule status, `done` when feedback exists
// (未成功·N天 is still missing — it needs the "没有约会成功" definition.)
export const dateOutcome = (p, isZh) => {
  if (!p.latest_date_status) return { text: isZh ? '未安排' : 'None', c: '#C9C3BA', title: isZh ? '还没有安排过约会' : 'No date scheduled yet' };
  const at = p.latest_date_at ? new Date(p.latest_date_at) : null;
  const past = at && !isNaN(at.getTime()) && at < new Date();
  const dead = ['cancelled', 'no_show', 'rejected'].includes(p.latest_date_status);
  if (past && !dead && !p.latest_date_has_feedback) {
    return { text: isZh ? '待反馈' : 'Feedback due', c: '#93591A', title: `${isZh ? '约会已过，反馈未写' : 'Date passed, feedback not written'} · ${at.toLocaleString()}` };
  }
  const ds = DATE_STATUS[p.latest_date_status] || { zh: p.latest_date_status, en: p.latest_date_status, c: '#6E685F' };
  return {
    text: isZh ? ds.zh : ds.en,
    done: !!p.latest_date_has_feedback,   // rendered as a Check icon in the pill
    c: ds.c,
    title: at && !isNaN(at.getTime()) ? `${isZh ? '最近约会' : 'Latest date'} · ${at.toLocaleString()}` : '',
  };
};

// 会员到期 — 还剩 N 天 / 已过期 N 天. parseCalDate keeps a DATE column on its
// stored calendar day for viewers west of UTC (see utils/calendarDate.js).
export const membershipDaysLeft = (p) => {
  const d = parseCalDate(p.membership_end_date);
  if (!d) return null;
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  return Math.round((startOf(d) - startOf(new Date())) / 86400000);
};

const membershipLeft = (p, isZh) => {
  const d = parseCalDate(p.membership_end_date);
  const days = membershipDaysLeft(p);
  if (days == null) return null;
  if (days < 0) return { label: isZh ? `已过期 ${-days}天` : `${-days}d over`, c: '#9E2B25', title: d.toLocaleDateString() };
  if (days <= 30) return { label: isZh ? `剩 ${days}天` : `${days}d left`, c: '#93591A', title: d.toLocaleDateString() };
  return { label: isZh ? `剩 ${days}天` : `${days}d left`, c: APP.muted, title: d.toLocaleDateString() };
};

// Row order inside a group answers "谁该被联系" — the board's whole point.
// 逾期 → 本周未联系 → 有安排的 → 其余, oldest touchpoint first within a tier.
const urgencyRank = (p) => {
  const at = p.next_contact_at ? new Date(p.next_contact_at) : null;
  const valid = at && !isNaN(at.getTime());
  if (valid && at < new Date()) return 0;
  if (!p.contacted_this_week) return 1;
  if (valid) return 2;
  return 3;
};
const byUrgency = (a, b) => {
  const ra = urgencyRank(a); const rb = urgencyRank(b);
  if (ra !== rb) return ra - rb;
  const ta = a.next_contact_at ? new Date(a.next_contact_at).getTime() : Infinity;
  const tb = b.next_contact_at ? new Date(b.next_contact_at).getTime() : Infinity;
  if (ta !== tb) return ta - tb;
  return Number(b.id) - Number(a.id);
};

const av = (bg, size = 24) => ({ width: size, height: size, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: size <= 22 ? 10 : 11, fontWeight: 700, color: '#FFFDF9', background: bg, flex: '0 0 auto', overflow: 'hidden' });

// Avatar: the profile photo when there is a usable one, initials otherwise.
// resolvePhotoUrl handles the three storage generations (uploads path, legacy
// host, bare base64) and returns null for placeholder junk.
function Avatar({ photo, initials, color, size = 24, title }) {
  const src = resolvePhotoUrl(photo);
  return (
    <div style={av(color, size)} title={title}>
      {src
        ? <img src={src} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        : initials}
    </div>
  );
}

const PAGE_SIZE = 12;

export default function ClientPipelineBoard({
  profiles, loading, onCardClick, onStageChange, isZh, language,
  oversight = false, truncatedAt,
  activeKey, onSelectKey, todayNode, todayLabel,
}) {
  const [draggingProfileId, setDraggingProfileId] = useState(null);
  const [page, setPage] = useState(1);
  const STAGES = STAGES_DEF(isZh);

  // Stage falls back to `new` so a profile written before the migration (or by
  // an older node still running the previous build) still lands in a real,
  // droppable group instead of the dead 「其他」 bucket.
  const norm = (p) => {
    const s = String(p.pipeline_stage || '').toLowerCase();
    return STAGES.some(x => x.key === s) ? s : 'new';
  };
  const byStage = {}; STAGES.forEach(s => { byStage[s.key] = []; });
  (profiles || []).forEach(p => { byStage[norm(p)].push(p); });
  // One stage at a time now: the stages are tabs in the table head rather than
  // stacked groups, so a coach sees a single flat, paginated list. Dropping a
  // dragged row on a TAB is what moves it between stages — with only one stage
  // rendered there is no other group on screen to drop onto.
  const stageTabs = STAGES.map(s => ({ ...s, count: byStage[s.key].length }));
  const firstNonEmpty = stageTabs.find(s => s.count > 0)?.key || STAGES[0].key;
  const current = activeKey || firstNonEmpty;
  const isToday = current === 'today';
  const rows = isToday ? [] : (byStage[current] || []).slice().sort(byUrgency);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pages);
  const pageRows = rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const sec = STAGES.find(s => s.key === current) || STAGES[0];

  const selectKey = (k) => { setPage(1); onSelectKey && onSelectKey(k); };

  const headerCells = [
    isZh ? '客户' : 'Client', isZh ? '负责教练' : 'Coach', isZh ? '阶段' : 'Stage',
    isZh ? '本周联系' : 'This week', isZh ? '下次联系' : 'Next', isZh ? '约会结果' : 'Date result',
    isZh ? '会员到期' : 'Expires', isZh ? '备注' : 'Notes',
  ];

  const Row = (p, sec) => {
    const on = p.can_chat === 1 || p.can_chat === true || p.can_chat === '1';
    const nm = (p.gcv && p.gcv['138']) || p.first_name || `#${p.id}`;
    const initials = `${(p.first_name || '')[0] || ''}${(p.last_name || '')[0] || ''}`.toUpperCase() || '?';
    const outcome = dateOutcome(p, isZh);
    const nc = fmtNextContact(p.next_contact_at, isZh);
    const ncTitle = nc ? `${p.next_contact_source === 'date' ? (isZh ? '约会' : 'Date') : (isZh ? '回访任务' : 'Follow-up')} · ${new Date(p.next_contact_at).toLocaleString()}` : '';
    const contacted = !!p.contacted_this_week;
    // 负责教练 = assigned_coach, NOT the matchmaker who recruited the client.
    // The pipeline is the coach's own worklist, so the person named in the row
    // has to be the one accountable for working it.
    const coach = p.coach_first_name || '';
    const exp = membershipLeft(p, isZh);
    const cell = { padding: '9px 10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12.5, color: APP.ink };
    return (
      <div key={p.id}
        onClick={() => onCardClick && onCardClick(p.id)}
        draggable
        onDragStart={(e) => { e.dataTransfer.setData('text/plain', String(p.id)); e.dataTransfer.effectAllowed = 'move'; setDraggingProfileId(p.id); }}
        onDragEnd={() => setDraggingProfileId(null)}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#F6F3EE'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = '#FFFDF9'; }}
        style={{ display: 'grid', gridTemplateColumns: COLS, alignItems: 'center', background: '#FFFDF9', borderTop: `1px solid ${APP.line}`, borderLeft: `4px solid ${sec.color}`, cursor: 'grab', opacity: draggingProfileId === p.id ? 0.45 : 1 }}>
        {/* 客户 */}
        <div style={{ ...cell, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Avatar photo={p.profile_photo} initials={initials} color={avColor(p.id)} size={26} />
          <div style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <span style={{ fontWeight: 600 }}>{nm}</span>{on && <Check size={13} strokeWidth={3} color="#3F7D5A" style={{ marginLeft: 3, verticalAlign: '-2px' }}>
              <title>{isZh ? '能聊的' : 'Can chat'}</title>
            </Check>}
            <small style={{ color: APP.muted, fontWeight: 500, marginLeft: 6, fontFamily: 'monospace' }}>#{displayIdOf(p)}</small>
          </div>
        </div>
        {/* 负责教练 */}
        <div style={{ ...cell, display: 'flex', alignItems: 'center', gap: 6 }}>
          {coach ? <><div style={av(avColor(p.coach_id), 22)}>{coach[0]}</div><span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{coach}</span></> : <span style={{ color: '#C9C3BA' }}>—</span>}
        </div>
        {/* 阶段 */}
        <div style={cell}><span style={pill(sec.color)}>{sec.label}</span></div>
        {/* 本周联系 */}
        <div style={{ ...cell, display: 'flex', justifyContent: 'center' }}>
          <span title={contacted ? (isZh ? '本周已联系' : 'Contacted this week') : (isZh ? '本周未联系' : 'Not contacted')}
            style={{ width: 18, height: 18, borderRadius: 2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, border: `2px solid ${contacted ? '#3F7D5A' : '#C9C3BA'}`, background: contacted ? '#3F7D5A' : '#FFFDF9', color: '#FFFDF9' }}>{contacted ? <Check size={11} strokeWidth={4} /> : null}</span>
        </div>
        {/* 下次联系 — next pending follow-up task or upcoming date, whichever is first */}
        <div style={{ ...cell }} title={ncTitle}>
          {nc ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 600, color: nc.overdue ? '#9E2B25' : APP.ink }}>
              {p.next_contact_source === 'date'
                ? <Heart size={12} fill="#9E2B25" color="#9E2B25" />
                : <Phone size={12} color={nc.overdue ? '#9E2B25' : APP.muted} />}
              {nc.label}
            </span>
          ) : <span style={{ color: '#C9C3BA' }}>—</span>}
        </div>
        {/* 约会结果 */}
        <div style={cell} title={outcome.title}>
          <span style={{ ...pill(outcome.c), gap: 3 }}>
            {outcome.text}{outcome.done && <Check size={11} strokeWidth={4} />}
          </span>
        </div>
        {/* 会员到期 */}
        <div style={{ ...cell, fontWeight: exp && exp.c !== APP.muted ? 700 : 500, color: exp ? exp.c : '#C9C3BA' }} title={exp ? exp.title : ''}>
          {exp ? exp.label : '—'}
        </div>
        {/* 备注 */}
        <div style={{ ...cell, color: p.staff_notes ? APP.muted : '#C9C3BA' }}>{p.staff_notes || '—'}</div>
      </div>
    );
  };

  const scopeChip = {
    fontSize: 11.5, color: APP.muted, background: '#FFFDF9', border: `1px solid ${APP.line}`,
    borderRadius: 2, padding: '3px 9px', fontWeight: 600,
  };

  return (
    <div style={{ background: APP.bg, border: '1px solid #D6D0C7', borderRadius: 2, boxShadow: 'none', overflow: 'hidden', color: APP.ink, fontSize: 13 }}>
      {/* toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderBottom: `1px solid ${APP.line}`, background: APP.alt, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 800, fontSize: 15, display: 'flex', alignItems: 'center', gap: 7 }}>
          <FolderKanban size={17} color={APP.burgundy} />{isZh ? '客户管道' : 'Client Pipeline'}
        </div>
        <span style={{ fontSize: 12, padding: '4px 10px', borderRadius: 2, background: '#FFFDF9', color: APP.burgundy, boxShadow: 'none', border: `1px solid ${APP.line}`, fontWeight: 600 }}>{isZh ? '看板' : 'Board'}</span>
        <div style={{ flex: 1 }} />
        {truncatedAt ? (
          <span title={isZh ? '结果已截断，请用筛选缩小范围' : 'Result truncated — narrow the filter'}
            style={{ ...scopeChip, color: '#93591A', borderColor: '#F0D9A8', background: '#F3EADB' }}>
            {isZh ? `仅显示前 ${truncatedAt} 位` : `First ${truncatedAt} only`}
          </span>
        ) : null}
        {/* Oversight roles still need to know they are looking at everybody's
            book; a coach does not need to be told these are their own clients,
            and does not need their own name badged back at them. */}
        {oversight && (
          <span style={scopeChip}>{isZh ? '全部教练的客户' : 'All coaches'}</span>
        )}
      </div>

      {/* ── table head: one tab per stage, plus 今日待办 ──────────────────
          Each tab is also a DROP TARGET. With a single stage on screen there
          is no other group to drag a row onto, so the tab strip is what moves
          a client between stages — hover it while dragging and it lights up. */}
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 2, padding: '0 10px', background: APP.alt, borderBottom: `1px solid ${APP.line}`, overflowX: 'auto' }}>
        {stageTabs.map(s => {
          const on = current === s.key;
          const dragging = draggingProfileId != null;
          return (
            <button key={s.key} type="button" onClick={() => selectKey(s.key)}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
              onDrop={(e) => {
                e.preventDefault();
                const id = e.dataTransfer.getData('text/plain');
                if (id && onStageChange) onStageChange(id, s.key);
                setDraggingProfileId(null);
              }}
              title={dragging ? (isZh ? `拖到这里 → ${s.label}` : `Drop here → ${s.label}`) : s.label}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', cursor: 'pointer',
                padding: '11px 13px', border: 'none', background: 'transparent', fontSize: 13,
                fontWeight: on ? 800 : 600, color: on ? APP.ink : APP.muted,
                borderBottom: `3px solid ${on ? s.color : 'transparent'}`,
                outline: dragging ? `2px dashed ${s.color}` : 'none', outlineOffset: -4,
                borderRadius: dragging ? 8 : 0,
              }}>
              <span style={{ width: 9, height: 9, borderRadius: 2, background: s.color, flex: '0 0 auto' }} />
              {s.label}
              <span style={{ fontSize: 11.5, fontWeight: 700, color: on ? APP.muted : '#6E685F' }}>{s.count}</span>
            </button>
          );
        })}
        {todayNode && (
          <button type="button" onClick={() => selectKey('today')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', cursor: 'pointer',
              padding: '11px 13px', border: 'none', background: 'transparent', fontSize: 13,
              fontWeight: isToday ? 800 : 600, color: isToday ? APP.ink : APP.muted,
              borderBottom: `3px solid ${isToday ? APP.burgundy : 'transparent'}`,
              marginLeft: 'auto', borderLeft: `1px solid ${APP.line}`,
            }}>
            <ClipboardCheck size={14} />{todayLabel || (isZh ? '今日待办' : 'Today')}
          </button>
        )}
      </div>

      {isToday ? todayNode : loading ? (
        <div style={{ textAlign: 'center', color: '#6E685F', padding: '3rem' }}>{isZh ? '加载中…' : 'Loading…'}</div>
      ) : !rows.length ? (
        <div style={{ textAlign: 'center', color: '#6E685F', padding: '3rem' }}>
          {isZh ? `「${sec.label}」阶段暂无客户` : `No clients in ${sec.label}`}
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 900 }}>
              {/* column header */}
              <div style={{ display: 'grid', gridTemplateColumns: COLS, alignItems: 'center', background: APP.alt, borderLeft: '4px solid transparent' }}>
                {headerCells.map((h, i) => (
                  <div key={i} style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: APP.muted, textTransform: 'uppercase', letterSpacing: '.04em', textAlign: i === 3 ? 'center' : 'left' }}>{h}</div>
                ))}
              </div>
              {pageRows.map(p => Row(p, sec))}
            </div>
          </div>
          {pages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderTop: `1px solid ${APP.line}` }}>
              <span style={{ fontSize: 12, color: APP.muted, fontWeight: 600 }}>
                {isZh ? `共 ${rows.length} 位客户` : `${rows.length} clients`}
              </span>
              <div style={{ flex: 1 }} />
              <button type="button" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)} style={pagerBtn(safePage <= 1)}>
                {isZh ? '‹ 上一页' : '‹ Prev'}
              </button>
              <span style={{ fontSize: 12, color: APP.muted, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{safePage} / {pages}</span>
              <button type="button" disabled={safePage >= pages} onClick={() => setPage(safePage + 1)} style={pagerBtn(safePage >= pages)}>
                {isZh ? '下一页 ›' : 'Next ›'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const pagerBtn = (disabled) => ({
  fontSize: 12, fontWeight: 700, padding: '6px 12px', borderRadius: 2,
  border: '1px solid #E4DFD7', background: '#FFFDF9', color: disabled ? '#C9C3BA' : '#111111',
  cursor: disabled ? 'not-allowed' : 'pointer',
});
